import React, { useEffect, useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  GENDER2,
  USER_PROFILE_DATA,
} from "../../facility-management-screen/constants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import { normalizeContactNumber } from "../../../utils/phoneNumberFormatter";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import MultipleSelectWithEvents from "../../../component-lib/Atoms/Select/MultiSelect";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { getRolesList } from "../../facility-management-screen/FacilityManagementSaga";
import FacilityManagementService from "../../../services/FacilityManagementService";
import { useDispatch, useSelector } from "react-redux";
import { componentKey } from "../../facility-management-screen/FacilityManagementSlice";

interface UsersScreenDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
  isEdit?: boolean;
  user?: string;
  defaultUsersData: any;
  facilityId: any;
}

export default function UsersScreenDialog({
  isOpen = false,
  handleClose,
  handleData,
  isEdit = false,
  user,
  defaultUsersData,
  facilityId,
}: UsersScreenDialogProps) {
  const { rolesList } = useSelector((state: any) => state[componentKey]);
  const dispatch = useDispatch();
  const [departmentList, setDepartmentList] = useState<any>([]);
  // All facilities loaded via fetch-all (no fixed limit) — feeds the Work
  // Location dropdown and department preselect.
  const [facilityAll, setFacilityAll] = useState<any>([]);

  const dialogHeading = isEdit ? `Edit ${user}` : `Add ${user}`;
  const validationSchema = Yup.object().shape({
    [USER_PROFILE_DATA.BSA_ID]: Yup.number()
      // .matches(VALIDATION_CONSTANTS.NUMERIC, "BSA ID must contain only numbers")
      .required("BSA ID is required"),
    [USER_PROFILE_DATA?.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [USER_PROFILE_DATA?.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name can only contain characters"
      )
      .required("Last name is required"),
    [USER_PROFILE_DATA?.ROLE]: Yup.string().required("Role is required"),
    [USER_PROFILE_DATA?.GENDER]: Yup.string().required("Gender is required"),
    [USER_PROFILE_DATA?.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [USER_PROFILE_DATA?.WORK_LOCATION]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.NAME,
        "Work location can only contain alphanumeric characters"
      )
      .required("Work location is required"),

    [USER_PROFILE_DATA?.DEPARTMENT]: Yup.array()
      .min(1, "At least one name must be selected")
      .required("This field is required"),

    [USER_PROFILE_DATA?.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
  });
  const userName = user === "Staff" ? "Nurse" : user;

  const role = rolesList?.map((data: any) => ({
    label: data?.name,
    value: data?.id,
  }));

  const workLocationData = facilityAll?.map((data: any) => ({
    label: data?.name,
    value: data?.id,
  }));

  // Page through ALL facilities (mirrors IncidentReportTab) so Work Location
  // is never truncated by a fixed page limit.
  const fetchAllFacilities = async () => {
    const first = await FacilityManagementService.getFacilityList({
      pageNumber: 1,
      limit: 100,
    });
    let rows: any[] = first?.data?.facilitys ?? [];
    const totalPages: number = first?.data?.totalPages ?? 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          FacilityManagementService.getFacilityList({
            pageNumber: i + 2,
            limit: 100,
          }).then((r: any) => r?.data?.facilitys ?? [])
        )
      );
      rows = rows.concat(...rest);
    }
    return rows;
  };

  useEffect(() => {
    (async () => {
      setFacilityAll(await fetchAllFacilities());
    })();
    const payload = {
      pageNumber: 1,
      limit: 200,
      type: user === "Staff" ? "staff" : "provider",
    };
    dispatch(getRolesList(payload));
  }, []);

  // Preselect the facility's departments once all facilities are loaded.
  useEffect(() => {
    if (facilityId && facilityAll?.length) {
      const facility = facilityAll.find((data: any) => data?.id === facilityId);
      const department = facility?.department?.length
        ? facility?.department?.map((data: any) => ({
            label: data?.name,
            value: data?.id,
          }))
        : [];
      setDepartmentList(department);
    }
  }, [facilityAll, facilityId]);

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [USER_PROFILE_DATA?.BSA_ID]: `${defaultUsersData?.bsaId || ""}`,
          [USER_PROFILE_DATA?.FIRST_NAME]: `${defaultUsersData?.firstName || ""}`,
          [USER_PROFILE_DATA?.LAST_NAME]: `${defaultUsersData?.lastName || ""}`,
          [USER_PROFILE_DATA?.EMAIL]: `${defaultUsersData?.email || ""}`,
          [USER_PROFILE_DATA.ROLE]: `${defaultUsersData?.role || ""}`,
          [USER_PROFILE_DATA?.GENDER]: `${defaultUsersData?.gender || ""}`,
          [USER_PROFILE_DATA?.WORK_LOCATION]: `${facilityId || ""}`,
          [USER_PROFILE_DATA?.DEPARTMENT]: defaultUsersData?.departments?.map(
            (data: any) => data?.department?.id
          ),
          [USER_PROFILE_DATA?.DEPARTMENT_NAME]:
            defaultUsersData?.departments?.map(
              (data: any) => data?.department?.id
            ),
          [USER_PROFILE_DATA?.CONTACT_NUMBER]: normalizeContactNumber(
            defaultUsersData?.contactNumber
          ),
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }: any) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                {/* <div className="pt-4">
                    <EventEHRUploadImageCard
                      imageUrl={null}
                      handleImage={(data) => {}}
                    />
                  </div> */}

                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      disabled={defaultUsersData?.bsaId ? true : false}
                      type="number"
                      textFieldLabel="BSA ID"
                      // isStarPresent={true}
                      name={USER_PROFILE_DATA.BSA_ID}
                      value={values[USER_PROFILE_DATA.BSA_ID]}
                      onChangeCb={(e) => {
                        setFieldValue(USER_PROFILE_DATA.BSA_ID, e.target.value);
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[USER_PROFILE_DATA.BSA_ID] &&
                        Boolean(errors[USER_PROFILE_DATA.BSA_ID])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.BSA_ID]
                          ? errors[USER_PROFILE_DATA.BSA_ID]
                          : undefined
                      }
                    />
                  </div>

                  <div className="pt-4">
                    <Input
                      textFieldLabel="First Name"
                      // placeholder=""
                      maxLength={20}
                      size="small"
                      name={USER_PROFILE_DATA.FIRST_NAME}
                      value={values[USER_PROFILE_DATA.FIRST_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          USER_PROFILE_DATA.FIRST_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[USER_PROFILE_DATA.FIRST_NAME] &&
                        Boolean(errors[USER_PROFILE_DATA.FIRST_NAME])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.FIRST_NAME]
                          ? errors[USER_PROFILE_DATA.FIRST_NAME]
                          : undefined
                      }
                    />
                  </div>

                  <Input
                    textFieldLabel="Last Name"
                    // placeholder=""
                    maxLength={20}
                    size="small"
                    name={USER_PROFILE_DATA.LAST_NAME}
                    value={values[USER_PROFILE_DATA.LAST_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        USER_PROFILE_DATA.LAST_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.LAST_NAME] &&
                      Boolean(errors[USER_PROFILE_DATA.LAST_NAME])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.LAST_NAME]
                        ? errors[USER_PROFILE_DATA.LAST_NAME]
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Email"
                    size="small"
                    name={USER_PROFILE_DATA.EMAIL}
                    value={values[USER_PROFILE_DATA.EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(USER_PROFILE_DATA.EMAIL, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.EMAIL] &&
                      Boolean(errors[USER_PROFILE_DATA.EMAIL])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.EMAIL]
                        ? errors[USER_PROFILE_DATA.EMAIL]
                        : undefined
                    }
                  />

                  <ContactInput
                    textFieldLabel="Contact Number"
                    name={USER_PROFILE_DATA.CONTACT_NUMBER}
                    value={values[USER_PROFILE_DATA.CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(USER_PROFILE_DATA.CONTACT_NUMBER, value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER] &&
                      Boolean(errors[USER_PROFILE_DATA.CONTACT_NUMBER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER]
                        ? errors[USER_PROFILE_DATA.CONTACT_NUMBER]
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  {/* SPECIALITY DROPDOWN */}
                  <SelectDropdown
                    label="Role"
                    value={values[USER_PROFILE_DATA.ROLE]}
                    options={role}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.ROLE, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.ROLE] &&
                      Boolean(errors[USER_PROFILE_DATA.ROLE])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.ROLE]
                        ? errors[USER_PROFILE_DATA.ROLE]
                        : undefined
                    }
                  />

                  {/* GENDER DROPDOWN */}
                  <SelectDropdown
                    label="Gender"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.GENDER]}
                    options={GENDER2}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.GENDER, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.GENDER] &&
                      Boolean(errors[USER_PROFILE_DATA.GENDER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.GENDER]
                        ? errors[USER_PROFILE_DATA.GENDER]
                        : undefined
                    }
                  />

                  <SelectDropdown
                    label="Work Location"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.WORK_LOCATION]}
                    options={workLocationData}
                    onChangeCb={(e) => {
                      const facility = facilityAll?.find(
                        (data: any) => data?.id === e.target.value
                      );

                      const department = facility?.department?.length
                        ? facility?.department?.map((data: any) => ({
                            label: data?.name,
                            value: data?.id,
                          }))
                        : [];
                      setDepartmentList([]);
                      setTimeout(() => {
                        setDepartmentList(department);
                      });

                      setFieldValue(USER_PROFILE_DATA.DEPARTMENT, []);

                      setFieldValue(
                        USER_PROFILE_DATA.WORK_LOCATION,
                        e.target.value
                      );
                    }}
                    error={
                      touched[USER_PROFILE_DATA.WORK_LOCATION] &&
                      Boolean(errors[USER_PROFILE_DATA.WORK_LOCATION])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.WORK_LOCATION]
                        ? errors[USER_PROFILE_DATA.WORK_LOCATION]
                        : undefined
                    }
                  />

                  <MultipleSelectWithEvents
                    options={departmentList}
                    defaultSelectedValues={
                      values[USER_PROFILE_DATA?.DEPARTMENT]
                    }
                    label="Department"
                    name={USER_PROFILE_DATA?.DEPARTMENT}
                    onItemsAdded={() => {}}
                    onItemsRemoved={() => {}}
                    onSelectionChanged={(selectedValues) => {
                      setFieldValue(
                        USER_PROFILE_DATA.DEPARTMENT,
                        selectedValues
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA?.DEPARTMENT] &&
                      Boolean(errors[USER_PROFILE_DATA?.DEPARTMENT])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA?.DEPARTMENT]
                        ? errors[USER_PROFILE_DATA?.DEPARTMENT]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {/* <div className="flex justify-end mt-10 gap-4"> */}
                {isEdit && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  //   disabled={!isValid} // Button disabled if form is invalid or submitting
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
